import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Lock, X, GraduationCap, Phone, KeyRound, CheckCircle, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SliderCaptcha from '../components/SliderCaptcha';
import { supabase } from '../supabase/client';

type Step = 'phone' | 'verify' | 'password' | 'success';

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  // 从 URL 参数或 location state 获取角色
  const initialRole = (location.state as { role?: string })?.role || 'student';
  const [role, setRole] = useState<'student' | 'teacher'>(initialRole);
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  // 步骤1：验证手机号是否存在
  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!captchaVerified) {
      setError('请先完成人机验证');
      return;
    }
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      setError('请输入正确的手机号');
      return;
    }

    setLoading(true);
    try {
      // 检查手机号是否已注册
      const { data: account } = await supabase
        .from('user_accounts')
        .select('id, mobile, role, auth_user_id')
        .eq('mobile', phone)
        .eq('role', role)
        .maybeSingle();

      if (!account) {
        // 尝试旧格式
        const { data: accountOld } = await supabase
          .from('user_accounts')
          .select('id, mobile, role, auth_user_id')
          .eq('mobile', `${phone}@${role}.meoo.local`)
          .eq('role', role)
          .maybeSingle();

        if (!accountOld) {
          setError('该手机号未注册' + (role === 'student' ? '学员' : '教师') + '账号');
          setLoading(false);
          return;
        }
      }

      // 教师需要检查审核状态
      if (role === 'teacher') {
        const { data: profile } = await supabase
          .from('profiles')
          .select('audit_status')
          .eq('phone', phone)
          .eq('role', 'teacher')
          .maybeSingle();

        if (profile?.audit_status === 'pending') {
          setError('您的账号正在审核中，审核通过后方可使用');
          setLoading(false);
          return;
        }
        if (profile?.audit_status === 'rejected') {
          setError('您的账号审核未通过，无法重置密码');
          setLoading(false);
          return;
        }
      }

      // 发送短信验证码（复用 message-service）
      const { data: sendData, error: sendError } = await supabase.functions.invoke('message-service', {
        body: { action: 'sms_send_code', phone }
      });

      if (sendError) {
        setError('验证码发送失败：' + (sendError.message || '请稍后重试'));
        setLoading(false);
        return;
      }

      if (!sendData || sendData.error) {
        setError('验证码发送失败：' + (sendData?.error || '请稍后重试'));
        setLoading(false);
        return;
      }

      if (!sendData.success) {
        setError('验证码发送失败，请稍后重试');
        setLoading(false);
        return;
      }

      setCountdown(60);
      setStep('verify');
    } catch (err: any) {
      setError('操作失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 步骤2：验证短信验证码
  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!code) {
      setError('请输入验证码');
      return;
    }

    setLoading(true);
    try {
      const { data: verifyData, error: verifyError } = await supabase.functions.invoke('message-service', {
        body: { action: 'sms_verify_code', phone, code }
      });

      if (verifyError) {
        setError(verifyError.message || '验证码验证失败');
        setLoading(false);
        return;
      }

      if (!verifyData || !verifyData.success) {
        setError(verifyData?.error || '验证码错误或已过期');
        setLoading(false);
        return;
      }

      setStep('password');
    } catch {
      setError('验证失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 步骤3：设置新密码
  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!newPassword) {
      setError('请输入新密码');
      return;
    }
    if (newPassword.length < 6) {
      setError('密码至少6位');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('两次密码输入不一致');
      return;
    }

    setLoading(true);
    try {
      // 用手机号 + 角色构造的邮箱登录，获取有效 session 后更新密码
      const email = `${phone}@${role}.meoo.local`;

      // 先用临时密码登录获取 session（因为不知道旧密码）
      // 方案：使用 Supabase admin API 更新密码
      // 但前端没有 admin 权限，所以需要通过 Edge Function 或其他方式

      // 通过 edu-service 的 reset_password action（公开接口，无需 JWT）
      const { data: resetData, error: resetError } = await supabase.functions.invoke('edu-service', {
        body: { action: 'reset_password', phone, role, newPassword }
      });

      if (resetError) {
        setError('密码重置失败：' + (resetError.message || '请稍后重试'));
        setLoading(false);
        return;
      }

      if (!resetData || !resetData.success) {
        setError(resetData?.error || '密码重置失败，请稍后重试');
        setLoading(false);
        return;
      }

      setStep('success');
    } catch {
      setError('密码重置失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 重新发送验证码
  const resendCode = async () => {
    if (countdown > 0) return;
    setError('');

    try {
      const { data: sendData, error: sendError } = await supabase.functions.invoke('message-service', {
        body: { action: 'sms_send_code', phone }
      });

      if (sendError || !sendData?.success) {
        setError('验证码发送失败，请稍后重试');
        return;
      }

      setCountdown(60);
    } catch {
      setError('验证码发送失败，请稍后重试');
    }
  };

  const getStepIcon = () => {
    switch (step) {
      case 'phone': return <Phone className="w-8 h-8 text-white" />;
      case 'verify': return <KeyRound className="w-8 h-8 text-white" />;
      case 'password': return <Lock className="w-8 h-8 text-white" />;
      case 'success': return <CheckCircle className="w-8 h-8 text-white" />;
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 'phone': return '重置密码';
      case 'verify': return '验证手机号';
      case 'password': return '设置新密码';
      case 'success': return '密码重置成功';
    }
  };

  const getStepColor = () => {
    switch (step) {
      case 'phone': return 'from-blue-500 to-cyan-400';
      case 'verify': return 'from-purple-500 to-indigo-400';
      case 'password': return 'from-green-500 to-emerald-400';
      case 'success': return 'from-green-500 to-emerald-400';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center py-12 px-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md bg-white rounded-2xl p-8 shadow-xl relative">
        <button onClick={() => navigate('/login')} className="absolute top-4 left-4 p-2 text-gray-400 hover:text-gray-600">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <button onClick={() => navigate('/')} className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-6">
          <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${getStepColor()} flex items-center justify-center mx-auto mb-4`}>
            {getStepIcon()}
          </div>
          <h1 className="text-2xl font-bold text-gray-800">{getStepTitle()}</h1>
          {step !== 'success' && (
            <p className="text-sm text-gray-500 mt-1">
              {role === 'student' ? '学员' : '教师'}账号密码重置
            </p>
          )}
        </div>

        {/* 步骤进度指示 */}
        {step !== 'success' && (
          <div className="flex items-center justify-center gap-2 mb-6">
            {(['phone', 'verify', 'password'] as Step[]).map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === s ? 'bg-blue-500 text-white' :
                  (['phone', 'verify', 'password'].indexOf(step) > i) ? 'bg-green-500 text-white' :
                  'bg-gray-200 text-gray-500'
                }`}>
                  {(['phone', 'verify', 'password'].indexOf(step) > i) ? '✓' : i + 1}
                </div>
                {i < 2 && <div className={`w-8 h-0.5 ${(['phone', 'verify', 'password'].indexOf(step) > i) ? 'bg-green-500' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>
        )}

        {/* 角色切换（仅在步骤1显示） */}
        {step === 'phone' && (
          <div className="flex gap-1 mb-6 justify-center">
            <button onClick={() => setRole('student')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${role === 'student' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              <Phone className="w-4 h-4" />学员
            </button>
            <button onClick={() => setRole('teacher')} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${role === 'teacher' ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              <GraduationCap className="w-4 h-4" />教师
            </button>
          </div>
        )}

        <AnimatePresence mode="wait">
          {step === 'phone' && (
            <motion.form key="phone" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} onSubmit={handlePhoneSubmit} className="space-y-4">
              {error && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</motion.div>}
              <div className="relative">
                <Phone className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" placeholder="请输入注册手机号" maxLength={11} />
              </div>
              <SliderCaptcha onVerify={setCaptchaVerified} />
              <button type="submit" disabled={loading} className="w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-medium hover:from-blue-500 hover:to-cyan-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? '验证中...' : '下一步'}
              </button>
            </motion.form>
          )}

          {step === 'verify' && (
            <motion.form key="verify" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} onSubmit={handleVerifyCode} className="space-y-4">
              {error && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</motion.div>}
              <div className="text-center text-sm text-gray-500 mb-2">
                验证码已发送至 <span className="font-medium text-gray-700">{phone}</span>
              </div>
              <div className="relative">
                <KeyRound className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={code} onChange={(e) => setCode(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none" placeholder="请输入短信验证码" maxLength={6} />
              </div>
              <div className="flex justify-center">
                <button type="button" onClick={resendCode} disabled={countdown > 0} className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400 disabled:cursor-not-allowed">
                  {countdown > 0 ? `${countdown}秒后可重新发送` : '重新发送验证码'}
                </button>
              </div>
              <button type="submit" disabled={loading} className="w-full py-3 bg-gradient-to-r from-purple-600 to-indigo-500 text-white rounded-lg font-medium hover:from-purple-500 hover:to-indigo-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? '验证中...' : '验证'}
              </button>
              <button type="button" onClick={() => { setStep('phone'); setCaptchaVerified(false); setCode(''); setError(''); }} className="w-full py-2 text-gray-500 hover:text-gray-700 text-sm">
                返回修改手机号
              </button>
            </motion.form>
          )}

          {step === 'password' && (
            <motion.form key="password" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 20 }} onSubmit={handleSetPassword} className="space-y-4">
              {error && <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</motion.div>}
              <div className="relative">
                <Lock className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" placeholder="请输入新密码（至少6位）" />
              </div>
              <div className="relative">
                <Lock className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" placeholder="请再次输入新密码" />
              </div>
              <button type="submit" disabled={loading} className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-500 text-white rounded-lg font-medium hover:from-green-500 hover:to-emerald-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? '重置中...' : '确认重置密码'}
              </button>
            </motion.form>
          )}

          {step === 'success' && (
            <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <p className="text-gray-600">密码已重置成功，请使用新密码登录</p>
              <button onClick={() => navigate('/login', { state: { role } })} className="w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-medium hover:from-blue-500 hover:to-cyan-400 transition-all">
                前往登录
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {step !== 'success' && (
          <div className="mt-6 text-center">
            <Link to="/login" className="text-blue-600 hover:text-blue-700 text-sm">返回登录</Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
